import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import {
  TrendingUp,
  Plus,
  Edit2,
  Trash2,
  Phone,
  MessageCircle,
  FileText,
  Paperclip,
  ChevronDown,
  X,
  Loader2,
  CheckCircle2,
  Check,
  ExternalLink,
  RefreshCw,
} from "lucide-react";
import api, { uploadFileUrl } from "@/lib/api";
import Modal from "./Modal";
import { fmtShekel, todayISO, dateInPeriod, taxIncluded, MONTHS_HE } from "@/lib/utils";
import { useSettings } from "@/contexts/SettingsContext";

const CAT_COLORS = ["#EFF5F1", "#FDEBDF", "#E3F2FD", "#F3E8FF", "#FEE2E2", "#DCFCE7", "#FEF3C7"];
const CAT_TEXT = ["#2C5E4E", "#9A3412", "#1D4ED8", "#6B21A8", "#B91C1C", "#166534", "#92400E"];

export default function IncomeCard({ month, year, refreshKey, onChange }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [openCat, setOpenCat] = useState(null); // category expanded
  const [editing, setEditing] = useState(null);
  const [newCatName, setNewCatName] = useState("");
  const [showNewCat, setShowNewCat] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const load = async () => {
    const [inc, cats] = await Promise.all([
      api.get("/incomes", { params: { month, year } }),
      api.get("/config/income-categories"),
    ]);
    setItems(inc.data);
    setCategories(cats.data.categories || []);
  };
  useEffect(() => {
    load();
  }, [month, year, refreshKey]);

  useEffect(() => {
    const handler = (e) => setCategories(e.detail);
    window.addEventListener("income-cats-updated", handler);
    return () => window.removeEventListener("income-cats-updated", handler);
  }, []);

  const total = items.reduce((s, x) => s + Number(x.amount || 0), 0);
  const { rates, taxRates } = useSettings();
  const taxableItems = items.filter((x) => x.is_taxable === true);
  const nonTaxableItems = items.filter((x) => x.is_taxable !== true);
  const totalTaxable = taxableItems.reduce((s, x) => s + Number(x.amount || 0), 0);
  const totalNonTaxable = nonTaxableItems.reduce((s, x) => s + Number(x.amount || 0), 0);
  const taxVat = taxIncluded(totalTaxable, rates.vat);
  const taxIncome = taxIncluded(totalTaxable, rates.income_tax);
  const taxBL = taxIncluded(totalTaxable, rates.social_security);
  const totalTaxes = taxVat + taxIncome + taxBL;

  const openNew = (cat) => {
    setEditing({
      id: null,
      category: cat || categories[0] || "",
      client_name: "",
      amount: "",
      date: dateInPeriod(month, year),
      contact_phone: "",
      whatsapp_phone: "",
      notes: "",
      quote_status: "",
      payment_demand_status: "",
      invoice_file: "",
      is_taxable: false,
      is_recurring: false,
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const dateStr = editing.date || todayISO();
      const [y, m] = dateStr.split("-").map((x) => parseInt(x, 10));
      const payload = {
        ...editing,
        category: editing.category || "כללי",
        client_name: editing.client_name || "ללא שם",
        amount: parseFloat(editing.amount) || 0,
        date: dateStr,
        month: m,
        year: y,
      };
      delete payload.id;
      delete payload.created_at;
      if (editing.id) await api.put(`/incomes/${editing.id}`, payload);
      else await api.post("/incomes", payload);
      setEditing(null);
      await load();
      onChange?.();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (e) {
      alert("שגיאה בשמירה: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/incomes/${id}`);
    load();
    onChange?.();
  };

  const toggleTaxable = async (item) => {
    const payload = {
      category: item.category,
      client_name: item.client_name,
      amount: item.amount,
      date: item.date,
      month: item.month,
      year: item.year,
      contact_phone: item.contact_phone || "",
      whatsapp_phone: item.whatsapp_phone || "",
      notes: item.notes || "",
      quote_status: item.quote_status || "",
      payment_demand_status: item.payment_demand_status || "",
      invoice_file: item.invoice_file || "",
      is_taxable: item.is_taxable !== true,
      is_recurring: item.is_recurring || false,
    };
    await api.put(`/incomes/${item.id}`, payload);
    await load();
    onChange?.();
  };

  const toggleRecurring = async (item) => {
    const payload = {
      category: item.category,
      client_name: item.client_name,
      amount: item.amount,
      date: item.date,
      month: item.month,
      year: item.year,
      contact_phone: item.contact_phone || "",
      whatsapp_phone: item.whatsapp_phone || "",
      notes: item.notes || "",
      quote_status: item.quote_status || "",
      payment_demand_status: item.payment_demand_status || "",
      invoice_file: item.invoice_file || "",
      is_taxable: item.is_taxable === true,
      is_recurring: !item.is_recurring,
    };
    await api.put(`/incomes/${item.id}`, payload);
    await load();
    onChange?.();
  };

  const deleteCategory = async (name) => {
    const { data } = await api.delete(`/config/income-categories`, { params: { name } });
    setCategories(data.categories);
    await load();
    onChange?.();
  };

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    const { data } = await api.post("/config/income-categories", { name: newCatName.trim() });
    setCategories(data.categories);
    setNewCatName("");
    setShowNewCat(false);
  };

  // build categories to display: known + any category with items
  const displayCats = Array.from(
    new Set([...categories, ...items.map((i) => i.category)])
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.2 }}
      className="card-surface p-6 md:p-7 col-span-full"
      data-testid="income-card"
    >
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center"
            style={{ background: "#DCFCE7", color: "var(--income)" }}
          >
            <TrendingUp size={20} />
          </div>
          <div>
            <div className="font-heebo font-bold text-lg text-slate-900">הכנסות</div>
            <div className="text-xs text-slate-500">
              {MONTHS_HE[month - 1]} {year} · לפי קטגוריות
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-end">
            <div className="text-xs uppercase text-slate-500 font-heebo">סה״כ הכנסות</div>
            <div className="num-pop text-2xl" style={{ color: "var(--income)" }} data-testid="income-total">
              {fmtShekel(total)}
            </div>
          </div>
          <button
            className="btn-primary text-sm"
            onClick={() => openNew()}
            data-testid="add-income-btn"
          >
            <Plus size={14} /> הכנסה חדשה
          </button>
        </div>
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-2 mb-5">
        {displayCats.map((c, i) => {
          const catItems = items.filter((it) => it.category === c);
          const catTotal = catItems.reduce((s, x) => s + Number(x.amount || 0), 0);
          const bg = CAT_COLORS[i % CAT_COLORS.length];
          const tx = CAT_TEXT[i % CAT_TEXT.length];
          const active = openCat === c;
          return (
            <div
              key={c}
              className="inline-flex items-center gap-1 chip"
              style={{
                background: bg,
                color: tx,
                boxShadow: active ? `0 0 0 2px ${tx}44` : "none",
              }}
              data-testid={`income-category-${c}`}
            >
              <button
                onClick={() => setOpenCat(active ? null : c)}
                className="inline-flex items-center gap-1"
              >
                {c} · {catItems.length}
                <span className="opacity-70">· {fmtShekel(catTotal)}</span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const catItemsCount = items.filter((it) => it.category === c).length;
                  const msg = catItemsCount > 0
                    ? `⚠️ למחוק את הקטגוריה "${c}" יחד עם כל ${catItemsCount} ההכנסות שבתוכה?`
                    : `למחוק את הקטגוריה "${c}"?`;
                  if (confirm(msg)) deleteCategory(c);
                }}
                className="opacity-60 hover:opacity-100 ms-1"
                title="מחק את הקטגוריה כולה"
                data-testid={`remove-income-category-${c}`}
              >
                <X size={12} />
              </button>
            </div>
          );
        })}
        {!showNewCat ? (
          <button
            className="chip"
            style={{ background: "#F1F5F9", color: "#334155" }}
            onClick={() => setShowNewCat(true)}
            data-testid="add-income-category-btn"
          >
            <Plus size={12} /> קטגוריה חדשה
          </button>
        ) : (
          <div className="inline-flex items-center gap-2">
            <input
              className="input-field text-xs py-1.5"
              placeholder="שם קטגוריה"
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              data-testid="new-income-category-input"
            />
            <button
              className="btn-primary text-xs py-1.5"
              onClick={addCategory}
              data-testid="save-income-category-btn"
            >
              שמור
            </button>
            <button className="btn-ghost text-xs" onClick={() => setShowNewCat(false)}>
              <X size={12} />
            </button>
          </div>
        )}
      </div>

      {/* List grouped by category */}
      <div className="space-y-4">
        {displayCats.length === 0 && (
          <div className="empty-state">אין עדיין קטגוריות. לחצו על &quot;קטגוריה חדשה&quot; כדי להתחיל.</div>
        )}
        {displayCats.map((c, i) => {
          const catItems = items.filter((it) => it.category === c);
          if (catItems.length === 0) return null;
          const bg = CAT_COLORS[i % CAT_COLORS.length];
          const tx = CAT_TEXT[i % CAT_TEXT.length];
          const isOpen = openCat === c || openCat === null;

          return (
            <div key={c} className="space-y-0">
              <div
                className="flex items-center justify-between p-4 rounded-t-2xl"
                style={{ background: bg + "60" }}
              >
                <div className="flex items-center gap-3">
                  <span className="chip" style={{ background: bg, color: tx }}>
                    {c}
                  </span>
                  <span className="text-sm text-slate-500">{catItems.length} לקוחות</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="num-pop text-slate-800">
                    {fmtShekel(catItems.reduce((s, x) => s + Number(x.amount || 0), 0))}
                  </div>
                  <button
                    onClick={() => setOpenCat(isOpen ? "____none____" : c)}
                    className="btn-ghost"
                    data-testid={`toggle-cat-${c}`}
                  >
                    <ChevronDown
                      size={16}
                      style={{ transform: isOpen ? "rotate(180deg)" : "" }}
                    />
                  </button>
                </div>
              </div>
              {isOpen && (
                <>
                  <IncomeTable
                    items={catItems}
                    onEdit={setEditing}
                    onDelete={del}
                    onToggleTaxable={toggleTaxable}
                    onToggleRecurring={toggleRecurring}
                    rates={rates}
                    testid={`income-table-${c}`}
                  />
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Grand tax-adjusted summary */}
      {items.length > 0 && (
        <>
          <SectionTaxSummaryIncome
            items={items}
            rates={rates}
            taxRates={taxRates}
            testid="income-tax-summary-all"
          />
          <div
          className="rounded-2xl p-5 border-2 mt-2"
          style={{
            background: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 60%, #F3E8FF 100%)",
            borderColor: "#6EE7B7",
          }}
          data-testid="incomes-grand-summary"
        >
          <div className="flex justify-between items-center flex-wrap gap-2">
            <span className="font-heebo font-bold text-slate-800 text-lg">סיכום הכנסות</span>
            <div className="text-end">
              <div className="text-xs text-slate-500 font-heebo">
                מוכרות במס: {fmtShekel(totalTaxable)} · פטורות: {fmtShekel(totalNonTaxable)}
              </div>
              <div className="num-pop text-3xl" style={{ color: "var(--income)" }}>
                {fmtShekel(total)}
              </div>
            </div>
          </div>

          {totalTaxable > 0 && (
            <div className="mt-4 rounded-xl bg-white/70 p-4 border" style={{ borderColor: "#6EE7B7" }} data-testid="incomes-tax-adjusted-summary">
              <div className="text-xs font-heebo text-slate-500 mb-2">
                ⬇ אחרי תשלום מסים ({taxableItems.length} הכנסות מסומנות)
              </div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-heebo text-slate-700">
                  מע״מ ({taxRates.vat}%)
                </span>
                <span className="num-pop" style={{ color: "#5B21B6" }} data-testid="incomes-vat-owed">
                  −{fmtShekel(taxVat)}
                </span>
              </div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-heebo text-slate-700">
                  מס הכנסה ({taxRates.income_tax}%)
                </span>
                <span className="num-pop" style={{ color: "#9D174D" }}>
                  −{fmtShekel(taxIncome)}
                </span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-heebo text-slate-700">
                  ביטוח לאומי ({taxRates.social_security}%)
                </span>
                <span className="num-pop" style={{ color: "#991B1B" }}>
                  −{fmtShekel(taxBL)}
                </span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-emerald-100">
                <span className="font-heebo font-bold text-slate-800">
                  הכנסה נטו אחרי מסים
                </span>
                <span className="num-pop text-2xl" style={{ color: "var(--income)" }} data-testid="incomes-net-after-tax">
                  {fmtShekel(total - totalTaxes)}
                </span>
              </div>
            </div>
          )}
        </div>
        </>
      )}

      <IncomeForm
        editing={editing}
        setEditing={setEditing}
        categories={displayCats}
        onSave={save}
      />
    </motion.div>
  );
}

function TaxTile({ label, value, bg, text }) {
  return (
    <div
      className="rounded-xl p-3 border"
      style={{ background: bg, borderColor: text + "44" }}
    >
      <div className="text-xs font-heebo" style={{ color: text }}>
        {label}
      </div>
      <div className="num-pop text-xl mt-1" style={{ color: text }}>
        {fmtShekel(value)}
      </div>
    </div>
  );
}

function IncomeTable({ items, onEdit, onDelete, onToggleTaxable, onToggleRecurring, testid, rates }) {
  const r = rates || { vat: 0.18, income_tax: 0.25, social_security: 0.05 };
  return (
    <div
      className="rounded-b-2xl border border-t-0 border-slate-200 overflow-hidden bg-white"
      data-testid={testid}
    >
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs">
          <tr>
            <th className="text-start p-3 font-heebo font-medium">שם</th>
            <th className="text-start p-3 font-heebo font-medium">תאריך</th>
            <th className="text-end p-3 font-heebo font-medium">סכום</th>
            <th className="p-3 font-heebo font-medium text-center hidden md:table-cell">
              מסים <span className="text-[10px]">(מע״מ · מ.ה. · ב״ל)</span>
            </th>
            <th className="p-3 font-heebo font-medium text-center">פעולות</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const isTaxable = it.is_taxable === true;
            const vat = isTaxable ? taxIncluded(it.amount, r.vat) : 0;
            const itax = isTaxable ? taxIncluded(it.amount, r.income_tax) : 0;
            const bl = isTaxable ? taxIncluded(it.amount, r.social_security) : 0;
            const totalTax = vat + itax + bl;
            const whatsappNum = (it.whatsapp_phone || it.contact_phone || "").replace(/[^\d]/g, "");
            const waHref = whatsappNum
              ? `https://wa.me/${whatsappNum.startsWith("0") ? "972" + whatsappNum.slice(1) : whatsappNum}`
              : null;
            return (
              <tr
                key={it.id}
                className="border-t border-slate-100 hover:bg-emerald-50/40 transition"
                data-testid={`income-row-${it.id}`}
              >
                <td className="p-3 text-slate-800 font-heebo">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{it.client_name}</span>
                    {it.is_recurring && (
                      <span
                        className="chip text-[10px] px-2 py-0.5"
                        style={{ background: "#DCFCE7", color: "#166534" }}
                      >
                        ↻ קבועה
                      </span>
                    )}
                    {it.invoice_file && (
                      <a
                        href={uploadFileUrl(it.invoice_file)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-emerald-600"
                        data-testid={`invoice-file-${it.id}`}
                        title="חשבונית / קבלה"
                      >
                        <Paperclip size={12} />
                      </a>
                    )}
                    {it.contact_phone && (
                      <a
                        href={`tel:${it.contact_phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="chip chip-info text-[10px] px-2 py-0.5"
                        data-testid={`call-${it.id}`}
                      >
                        <Phone size={10} /> {it.contact_phone}
                      </a>
                    )}
                    {waHref && (
                      <a
                        href={waHref}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="chip text-[10px] px-2 py-0.5"
                        style={{ background: "#DCFCE7", color: "#128C7E" }}
                        data-testid={`whatsapp-${it.id}`}
                      >
                        <MessageCircle size={10} /> WhatsApp
                      </a>
                    )}
                    {it.quote_status === "sent" && (
                      <span className="chip chip-warn text-[10px] px-2 py-0.5">
                        הצעת מחיר
                      </span>
                    )}
                    {it.payment_demand_status === "sent" && (
                      <span className="chip chip-purple text-[10px] px-2 py-0.5">
                        דרישת תשלום
                      </span>
                    )}
                  </div>
                  {it.notes && (
                    <div className="text-xs text-slate-400 mt-0.5">{it.notes}</div>
                  )}
                </td>
                <td className="p-3 text-slate-500">{it.date}</td>
                <td
                  className="p-3 text-end num-pop"
                  style={{ color: "var(--income)" }}
                  data-testid={`income-amount-${it.id}`}
                >
                  {fmtShekel(it.amount)}
                </td>
                <td className="p-2 text-center hidden md:table-cell">
                  {isTaxable ? (
                    <div className="inline-flex flex-col items-center gap-0.5">
                      <div className="text-[10px] text-slate-500 font-heebo">
                        {fmtShekel(vat)} · {fmtShekel(itax)} · {fmtShekel(bl)}
                      </div>
                      <div className="num-pop text-amber-700 text-sm">
                        סה״כ {fmtShekel(totalTax)}
                      </div>
                      <div className="num-pop text-emerald-700 text-sm font-bold" data-testid={`income-net-${it.id}`}>
                        נטו {fmtShekel(it.amount - totalTax)}
                      </div>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">לא חייב</span>
                  )}
                </td>
                <td className="p-2 whitespace-nowrap text-center">
                  <div className="flex items-center gap-1 justify-center">
                    <button
                      type="button"
                      className={`p-2 rounded-lg transition ${
                        isTaxable
                          ? "text-emerald-700 bg-emerald-100 hover:bg-emerald-200"
                          : "text-slate-300 hover:bg-slate-100 hover:text-slate-500 border-2 border-dashed border-slate-300"
                      }`}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await onToggleTaxable(it);
                        } catch (err) {
                          alert("שגיאה: " + (err?.message || err));
                        }
                      }}
                      data-testid={`toggle-income-taxable-${it.id}`}
                      title={
                        isTaxable
                          ? "מוכר במס - לחץ כדי להסיר מחישוב"
                          : "לחץ כדי לסמן כמוכר במס"
                      }
                    >
                      <Check size={16} />
                    </button>
                    <button
                      type="button"
                      className={`p-2 rounded-lg transition ${
                        it.is_recurring
                          ? "text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                          : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                      }`}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await onToggleRecurring(it);
                        } catch (err) {
                          alert("שגיאה: " + (err?.message || err));
                        }
                      }}
                      data-testid={`toggle-income-recurring-${it.id}`}
                      title={
                        it.is_recurring
                          ? "בטל קביעות - לא יופיע בחודשים הבאים"
                          : "הפוך לקבוע - יופיע כל חודש אוטומטית"
                      }
                    >
                      <RefreshCw size={16} />
                    </button>
                    <button
                      type="button"
                      className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition"
                      onClick={() => onEdit(it)}
                      data-testid={`edit-income-${it.id}`}
                      title="ערוך"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      type="button"
                      className="p-2 rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700 transition"
                      onClick={async () => {
                        try {
                          await onDelete(it.id);
                        } catch (err) {
                          alert("שגיאה במחיקה: " + (err?.message || err));
                        }
                      }}
                      data-testid={`delete-income-${it.id}`}
                      title="מחק"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function MiniTaxTile({ label, value, bg, text, bold }) {
  return (
    <div
      className="rounded-lg p-2 border"
      style={{ background: bg, borderColor: text + "33" }}
    >
      <div className="text-[10px] font-heebo" style={{ color: text }}>
        {label}
      </div>
      <div
        className={`num-pop mt-0.5 ${bold ? "text-lg" : "text-base"}`}
        style={{ color: text }}
      >
        {fmtShekel(value)}
      </div>
    </div>
  );
}

function SectionTaxSummaryIncome({ items, testid, rates, taxRates }) {
  const marked = (items || []).filter((x) => x.is_taxable === true);
  if (marked.length === 0) return null;
  const base = marked.reduce((s, x) => s + Number(x.amount || 0), 0);
  const vat = taxIncluded(base, rates.vat);
  const itax = taxIncluded(base, rates.income_tax);
  const bl = taxIncluded(base, rates.social_security);
  const total = vat + itax + bl;
  return (
    <div
      className="rounded-2xl border-2 p-4 mt-3"
      style={{
        background: "linear-gradient(135deg, #FAF5FF 0%, #F3E8FF 100%)",
        borderColor: "#C4B5FD",
      }}
      data-testid={testid}
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="font-heebo font-bold text-purple-900 text-sm">
            סיכום מוכר במס · {marked.length} {marked.length === 1 ? "רשומה" : "רשומות"}
          </div>
          <div className="text-[11px] text-purple-800 font-heebo mt-0.5">
            מבוסס רק על פריטים שסומנו ב-✓
          </div>
        </div>
        <div className="text-end">
          <div className="text-[11px] text-purple-800 font-heebo">בסיס חייב</div>
          <div className="num-pop text-lg text-purple-900">
            {fmtShekel(base)}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <MiniTaxTile label={`מע״מ ${taxRates.vat}%`} value={vat} bg="#EDE9FE" text="#5B21B6" />
        <MiniTaxTile label={`מס הכנסה ${taxRates.income_tax}%`} value={itax} bg="#FCE7F3" text="#9D174D" />
        <MiniTaxTile label={`ביטוח לאומי ${taxRates.social_security}%`} value={bl} bg="#FEE2E2" text="#991B1B" />
        <MiniTaxTile label="סה״כ מסים" value={total} bg="#F5D0FE" text="#701A75" bold />
      </div>
    </div>
  );
}

function IncomeForm({ editing, setEditing, categories, onSave, saving }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [justUploaded, setJustUploaded] = useState(false);

  if (!editing) return null;

  const uploadFile = async (file) => {
    setUploading(true);
    setJustUploaded(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditing((v) => ({ ...v, invoice_file: data.filename }));
      setJustUploaded(true);
      setTimeout(() => setJustUploaded(false), 2500);
    } catch (e) {
      alert("שגיאה בהעלאת קובץ");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      open={!!editing}
      onClose={() => setEditing(null)}
      title={editing.id ? "עריכת הכנסה" : "הכנסה חדשה"}
      testId="income-modal"
      footer={
        <>
          <button
            className="btn-primary"
            onClick={onSave}
            disabled={saving}
            data-testid="save-income-btn"
          >
            {saving ? (
              <>
                <Loader2 size={14} className="animate-spin" /> שומר…
              </>
            ) : (
              "שמור"
            )}
          </button>
          <button
            className="btn-secondary"
            onClick={() => setEditing(null)}
            disabled={saving}
          >
            ביטול
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-xs text-slate-500 mb-1 font-heebo">קטגוריה (אופציונלי)</div>
            <div className="flex gap-2">
              <select
                className="input-field flex-1"
                value={editing.category || ""}
                onChange={(e) => setEditing((v) => ({ ...v, category: e.target.value }))}
                data-testid="income-category-select"
              >
                <option value="">-- ללא קטגוריה --</option>
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn-secondary text-xs px-2"
                onClick={async () => {
                  const name = prompt("שם קטגוריה חדשה:");
                  if (!name || !name.trim()) return;
                  try {
                    const { data } = await api.post("/config/income-categories", {
                      name: name.trim(),
                    });
                    setEditing((v) => ({ ...v, category: name.trim() }));
                    window.dispatchEvent(new CustomEvent("income-cats-updated", { detail: data.categories }));
                  } catch (e) {
                    alert("שגיאה: " + (e?.response?.data?.detail || e.message));
                  }
                }}
                data-testid="add-category-in-form-btn"
                title="הוסף קטגוריה חדשה"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1 font-heebo">תאריך</div>
            <input
              type="date"
              className="input-field"
              value={editing.date}
              onChange={(e) => setEditing((v) => ({ ...v, date: e.target.value }))}
              data-testid="income-date-input"
            />
          </div>
        </div>

        {/* Taxable toggle */}
        <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
          <input
            type="checkbox"
            id="income-taxable"
            checked={editing.is_taxable !== false}
            onChange={(e) => setEditing((v) => ({ ...v, is_taxable: e.target.checked }))}
            className="w-4 h-4"
            data-testid="income-taxable-toggle"
          />
          <label htmlFor="income-taxable" className="text-sm font-heebo cursor-pointer flex-1">
            <span className="font-semibold text-amber-900">הכנסה חייבת במס</span>
            <span className="text-amber-700 text-xs block mt-0.5">
              יש להוציא חשבונית מס · יחושב מע&quot;מ 18%, מס הכנסה 25%, ב&quot;ל 5%
            </span>
          </label>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1 font-heebo">שם לקוח / תיאור</div>
          <input
            className="input-field"
            value={editing.client_name}
            onChange={(e) => setEditing((v) => ({ ...v, client_name: e.target.value }))}
            data-testid="income-client-input"
          />
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1 font-heebo">סכום (₪)</div>
          <input
            type="number"
            step="0.01"
            className="input-field"
            value={editing.amount}
            onChange={(e) => setEditing((v) => ({ ...v, amount: e.target.value }))}
            data-testid="income-amount-input"
          />
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1 font-heebo">
            טלפון איש קשר <span className="text-emerald-600">· ה-WhatsApp יוצר אוטומטית</span>
          </div>
          <input
            className="input-field"
            value={editing.contact_phone}
            onChange={(e) => setEditing((v) => ({ ...v, contact_phone: e.target.value }))}
            placeholder="050-1234567"
            data-testid="income-phone-input"
            dir="ltr"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex items-center gap-2 p-3 rounded-xl bg-slate-50 cursor-pointer">
            <input
              type="checkbox"
              checked={editing.quote_status === "sent"}
              onChange={(e) =>
                setEditing((v) => ({
                  ...v,
                  quote_status: e.target.checked ? "sent" : "",
                }))
              }
              data-testid="quote-sent-checkbox"
            />
            <span className="text-sm font-heebo">נשלחה הצעת מחיר</span>
          </label>
          <label className="flex items-center gap-2 p-3 rounded-xl bg-slate-50 cursor-pointer">
            <input
              type="checkbox"
              checked={editing.payment_demand_status === "sent"}
              onChange={(e) =>
                setEditing((v) => ({
                  ...v,
                  payment_demand_status: e.target.checked ? "sent" : "",
                }))
              }
              data-testid="payment-demand-checkbox"
            />
            <span className="text-sm font-heebo">נשלחה דרישת תשלום</span>
          </label>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1 font-heebo">חשבונית / קבלה (קובץ)</div>
          <div
            className="rounded-2xl p-3 border-2 border-dashed flex items-center justify-between flex-wrap gap-2"
            style={{
              borderColor: editing.invoice_file ? "#22C55E" : "#98CBB4",
              background: editing.invoice_file ? "#F0FDF4" : "#F0F7F2",
            }}
          >
            <div className="flex items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])}
                data-testid="invoice-file-input"
              />
              <button
                type="button"
                className="btn-secondary text-sm"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="upload-invoice-btn"
              >
                {uploading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Paperclip size={14} />
                )}
                {uploading
                  ? "מעלה…"
                  : editing.invoice_file
                  ? "החלף קובץ"
                  : "העלה קובץ"}
              </button>
              {editing.invoice_file && (
                <button
                  type="button"
                  className="btn-ghost text-xs text-red-600"
                  onClick={() => setEditing((v) => ({ ...v, invoice_file: "" }))}
                >
                  <X size={12} /> הסר
                </button>
              )}
              {justUploaded && (
                <span
                  className="chip text-xs"
                  style={{ background: "#DCFCE7", color: "#166534" }}
                >
                  <CheckCircle2 size={12} /> הועלה
                </span>
              )}
            </div>
            {editing.invoice_file && !uploading && (
              <a
                href={uploadFileUrl(editing.invoice_file)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl font-heebo font-semibold text-xs shadow-md hover:shadow-lg transition"
                style={{
                  background: "linear-gradient(135deg, #22C55E, #16A34A)",
                  color: "#fff",
                }}
                data-testid="income-open-file"
              >
                <ExternalLink size={12} /> פתח קובץ שעלה
              </a>
            )}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500 mb-1 font-heebo">הערות</div>
          <textarea
            className="input-field"
            rows={2}
            value={editing.notes}
            onChange={(e) => setEditing((v) => ({ ...v, notes: e.target.value }))}
          />
        </div>
      </div>
    </Modal>
  );
}
